import { DEFAULT_WORKTREE_SETTINGS, type WorktreeSettings } from '@crowdex/agent-contracts/api';
import { useCallback, useEffect, useState } from 'react';
import { readWorktreeSettings, saveWorktreeSettings } from '@/lib/local-conversations';

/**
 * This machine's managed-worktree settings (folder, auto-cleanup, kept count), kept by its server
 * so that every window and device makes and cleans worktrees alike. Loaded on mount (not in the
 * preview); each change is saved at once.
 */
export function useWorktreeSettings(preview: boolean) {
  const [settings, setSettings] = useState<WorktreeSettings>(DEFAULT_WORKTREE_SETTINGS);

  useEffect(() => {
    if (preview) return;
    let live = true;
    void readWorktreeSettings().then(
      (loaded) => live && setSettings(loaded),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [preview]);

  const change = useCallback((next: Partial<WorktreeSettings>) => {
    setSettings((current) => {
      const updated = { ...current, ...next };
      void saveWorktreeSettings(updated).catch(() => undefined);
      return updated;
    });
  }, []);

  return {
    worktreeRoot: settings.root ?? '',
    worktreeAutoCleanup: settings.autoCleanup,
    worktreeKeepCount: settings.keepCount,
    // Kept as typed; the server trims what it saves.
    setWorktreeRoot: (value: string) => change({ root: value === '' ? null : value }),
    setWorktreeAutoCleanup: (value: boolean) => change({ autoCleanup: value }),
    setWorktreeKeepCount: (value: number) => change({ keepCount: value }),
  };
}
